import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FiYoutube, FiSave, FiLoader } from "react-icons/fi";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const AdminFabArt = () => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const fetchYoutubeUrl = async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/fabart`);
        if (res.data.youtubeUrl) {
          setYoutubeUrl(res.data.youtubeUrl);
        }
      } catch (err) {
        console.error(err);
        setErrorMsg("Erreur de chargement de l'URL.");
      }
    };

    fetchYoutubeUrl();
  }, []);

  const transformYoutubeUrl = (url) => {
    const match = url.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/
    );
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    const finalUrl = transformYoutubeUrl(youtubeUrl);

    if (!finalUrl.includes("/embed/")) {
      setErrorMsg("URL YouTube invalide ou non prise en charge.");
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${apiUrl}/api/fabart`, { youtubeUrl: finalUrl });
      setSuccessMsg("L'URL a été mise à jour avec succès !");
      setYoutubeUrl(finalUrl);
    } catch (error) {
      setErrorMsg(
        "Erreur lors de la mise à jour : " +
          (error.response?.data?.message || error.message)
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="admin-container"
    >
      <div className="admin-header">
        <FiYoutube className="icon-youtube" />
        <h2>Modifier la vidéo YouTube</h2>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-group">
          <label htmlFor="youtubeUrl">URL YouTube</label>
          <input
            type="text"
            id="youtubeUrl"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            required
          />
          <small className="hint">
            Collez l'URL complète de la vidéo YouTube
          </small>
        </div>

        <motion.button
          type="submit"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          disabled={loading}
          className="submit-button"
        >
          {loading ? (
            <>
              <FiLoader className="spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <FiSave />
              Enregistrer
            </>
          )}
        </motion.button>
      </form>

      {successMsg && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="success-message"
        >
          {successMsg}
        </motion.div>
      )}

      {errorMsg && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="error-message"
        >
          {errorMsg}
        </motion.div>
      )}

      <style jsx>{`
        .admin-container {
          max-width: 600px;
          margin: 2rem auto;
          padding: 2.5rem;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          font-family: "Segoe UI", sans-serif;
        }

        .admin-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          color: #ff0000;
        }

        .admin-header h2 {
          margin: 0;
          font-size: 1.5rem;
          color: #333;
        }

        .icon-youtube {
          font-size: 2rem;
        }

        .admin-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        label {
          font-weight: 600;
          color: #555;
        }

        input {
          padding: 0.8rem 1rem;
          font-size: 1rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          transition: border 0.3s;
        }

        input:focus {
          outline: none;
          border-color: #ff0000;
          box-shadow: 0 0 0 2px rgba(255, 0, 0, 0.1);
        }

        .hint {
          color: #888;
          font-size: 0.85rem;
        }

        .submit-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.8rem 1.5rem;
          background: #ff0000;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.3s;
        }

        .submit-button:hover {
          background: #cc0000;
        }

        .submit-button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .success-message {
          margin-top: 1.5rem;
          padding: 0.8rem;
          background: #e6ffed;
          color: #28a745;
          border-radius: 6px;
          text-align: center;
        }

        .error-message {
          margin-top: 1.5rem;
          padding: 0.8rem;
          background: #ffe6e6;
          color: #dc3545;
          border-radius: 6px;
          text-align: center;
        }
      `}</style>
    </motion.div>
  );
};

export default AdminFabArt;
